import React from 'react';
import { useQuery } from 'react-query';
import { snakeCase } from 'lodash';
import http from '../../../utils/http';
import { useTranslation } from 'utils/translation';
import { Option } from '../types';
import { useShallowEqualEffect } from '../../../utils/use-shallow-effect';
import { useFiltersContext } from '../../../components/v2/Filters/FiltersContext';
import { Pagination } from '../../@common/crud/types';

const getDefaultOptionsUrl = (name: string) => `/api/reports/options/${name}`;

const useSelectAdapterOptionsQuery = ({
  id,
  name,
  url,
  dependencies = [],
  hasPagination = false,
  searchQuery = '',
}: {
  id?: string;
  name: string;
  url: string | null;
  dependencies?: string[];
  hasPagination?: boolean;
  searchQuery?: string;
}) => {
  const { i18n } = useTranslation();
  const filters = useFiltersContext();
  const values = filters.values;
  const queryUrl = url || getDefaultOptionsUrl(name);

  const deps = React.useMemo(
    () =>
      Object.fromEntries(
        dependencies.map((key: keyof typeof values) => [
          key,
          values[key] || null,
        ]),
      ),
    [values, dependencies],
  );

  const relatedParams = React.useMemo(
    () =>
      Object.keys(values)
        // Free-text search is not a select dependency; including it can
        // change/empty option lists and break restored selected values.
        .filter((key: keyof typeof values) => key !== name && key !== 'query')
        .reduce((prev: any, key) => {
          prev[key] = values[key] || null;

          return prev;
        }, {}),
    [values, name],
  );

  const query = useQuery(
    `report_params_select_options_${i18n.language}_${name}_${snakeCase(queryUrl)}`,
    async () => {
      let serverOptions = await http.get<never, Option[]>(queryUrl, {
        params: {
          ...deps,
          id,
          query: searchQuery,
          params: JSON.stringify(relatedParams),
        },
      });

      if (hasPagination) {
        serverOptions = (serverOptions as unknown as Pagination<Option>).data;
      }

      return serverOptions.map((item) => ({
        id: String(item.id),
        label: item.name,
      }));
    },
  );

  useShallowEqualEffect(() => {
    void query.refetch();
  }, [deps, relatedParams, query.refetch]);

  return query;
};

export default useSelectAdapterOptionsQuery;
